import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Alert, Box, Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import { useBlocker, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { useQueryClient } from "react-query";
import {
    actionPlanTemplateQueryKey,
    fetchActionPlanTemplate,
    useUpdateActionPlanTemplate,
    UpdateActionPlanTemplateSchema,
    type TemplateDetail,
    type TemplateAction,
} from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { URLHandler } from "src/services/URLHandler";
import { useSnackbar } from "src/utils/useSnackbar";
import { isStaleTemplateVersion, templateErrorReasons } from "src/api/client-api/actionPlanTemplates/templateErrors";
import { TemplateActionDialog } from "./TemplateActionDialog";
import { moveTemplateAction, removeTemplateAction, replaceTemplateAction } from "./templateActionForm";
import { TemplateGeneralInformationForm } from "./TemplateGeneralInformationForm";
import { TemplateEditableActions } from "./TemplateEditableActions";
import { TemplateEditorHeader } from "./TemplateEditorHeader";
import { TemplateUnsavedChangesDialog } from "./TemplateUnsavedChangesDialog";
import { useTemplateSectorPages } from "src/api/client-api/actionPlanTemplates/useTemplateCreationOptions";
import { useGetOneOrganizationSector } from "src/api/client-api/companySectors/useGetOneOrganizationSector";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

type Props = { template: TemplateDetail };

export const TemplateEditor = (props: Props) => {
    const { template } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();
    const mutation = useUpdateActionPlanTemplate(template.id);
    const sectorSearch = useDelayedSearch({ defaultDelay: 300 });
    const sectors = useTemplateSectorPages(template.ssqId, sectorSearch.delayedSearch);
    const savedSector = useGetOneOrganizationSector({ sectorId: template.sectorId });

    const [name, setName] = useState(template.name);
    const [version, setVersion] = useState(template.version);
    const [selectedSector, setSelectedSector] = useState<{ id: number; name: string } | null>({
        id: template.sectorId,
        name: template.sectorName,
    });
    const sectorId = selectedSector?.id;
    const [actions, setActions] = useState<TemplateAction[]>(template.actions);

    const snapshot = JSON.stringify({ name, sectorId, actions });
    const [saved, setSaved] = useState(snapshot);
    const dirty = snapshot !== saved;

    const [dialog, setDialog] = useState<"add" | TemplateAction | null>(null);
    const blocker = useBlocker(dirty);
    const blockerRef = useRef(blocker);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const listUrl = URLHandler.settings.configurationPath("platform-setup", "action-plan-templates");
    const leave = () => navigate(`${listUrl}/${template.id}`);

    const [submitting, setSubmitting] = useState(false);
    const busy = submitting || mutation.isLoading;
    const sectorIsKnownToBeDetached =
        sectorId === template.sectorId &&
        ((savedSector.isSuccess &&
            savedSector.sector?.ssqs !== undefined &&
            !savedSector.sector.ssqs.some((association) => association.ssqId === template.ssqId)) ||
            (isAxiosError(savedSector.error) && savedSector.error.response?.status === 404));
    const valid =
        UpdateActionPlanTemplateSchema.safeParse({ name, sectorId, version, actions }).success &&
        !sectorIsKnownToBeDetached;

    useLayoutEffect(() => {
        blockerRef.current = blocker;
    }, [blocker]);

    useEffect(() => {
        if (!dirty) {
            return;
        }

        const beforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
        };

        window.addEventListener("beforeunload", beforeUnload);

        return () => window.removeEventListener("beforeunload", beforeUnload);
    }, [dirty]);

    const reloadAfterConcurrentChange = async () => {
        try {
            const fresh = await queryClient.fetchQuery(actionPlanTemplateQueryKey(template.id), () =>
                fetchActionPlanTemplate(template.id),
            );

            setName(fresh.name);
            setVersion(fresh.version);
            setSelectedSector({ id: fresh.sectorId, name: fresh.sectorName });
            setActions(fresh.actions);
            setSaved(JSON.stringify({ name: fresh.name, sectorId: fresh.sectorId, actions: fresh.actions }));
            mutation.reset();
            snackbar.setSnackbarProps({
                type: "error",
                message: t(
                    "settings.action_plan_templates.editor.stale_version",
                    "Someone else changed this template. It has been reloaded with the latest version: review it and apply your changes again.",
                ),
            });
        } catch {
            setErrors({
                version: t(
                    "settings.action_plan_templates.editor.stale_version_reload_failed",
                    "Someone else changed this template and it could not be reloaded. Refresh the page before saving again.",
                ),
            });
        }
    };

    const save = async () => {
        if (!valid || busy) {
            return;
        }

        setErrors({});
        setSubmitting(true);

        try {
            const result = await mutation.mutateAsync(
                UpdateActionPlanTemplateSchema.parse({ name, sectorId, version, actions }),
            );

            setName(result.name);
            setVersion(result.version);
            setSelectedSector({ id: result.sectorId, name: result.sectorName });
            setActions(result.actions);
            setSaved(JSON.stringify({ name: result.name, sectorId: result.sectorId, actions: result.actions }));

            snackbar.setSnackbarProps({
                type: "success",
                message: t("settings.action_plan_templates.editor.saved", '"{{name}}" saved.', { name: result.name }),
            });

            // Navigation may have been blocked while the save request was pending.
            const pendingNavigation = blockerRef.current;
            if (pendingNavigation.state === "blocked") {
                pendingNavigation.proceed();
            }
        } catch (error) {
            const next: Record<string, string> = {};

            if (isStaleTemplateVersion(error)) {
                await reloadAfterConcurrentChange();
                return;
            }

            if (templateErrorReasons(error, 422).actions === "at_least_one_action_required") {
                next.actions = t(
                    "settings.action_plan_templates.editor.at_least_one_action_required",
                    "A published template needs at least one action.",
                );
            }

            if (isAxiosError(error)) {
                for (const violation of error.response?.data?.violations ?? []) {
                    if (typeof violation.propertyPath === "string" && typeof violation.message === "string") {
                        next[violation.propertyPath] = violation.message;
                    }
                }

                if (
                    error.response?.status === 409 &&
                    error.response.data?.reasons?.sectorId === "published_template_exists"
                ) {
                    const id = error.response.data?.conflictingTemplateId;
                    let conflictName: string | undefined;

                    if (typeof id === "string") {
                        try {
                            const response = await loggedInApiCall.get(
                                `/api/action-plan-templates/${encodeURIComponent(id)}`,
                            );
                            conflictName = response.data.name;
                        } catch {
                            /* Keep the conflict visible even if the other template cannot be read. */
                        }
                    }

                    next.sectorId =
                        conflictName ?
                            t(
                                "settings.action_plan_templates.editor.sector_conflict_named",
                                'The template "{{name}}" is already published for this SSQ and industry sector.',
                                { name: conflictName },
                            )
                        :   t(
                                "settings.action_plan_templates.editor.sector_conflict",
                                "A template is already published for this SSQ and industry sector.",
                            );
                }
            }

            setErrors(next);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: theme.spacing(2), md: theme.spacing(4) } }}>
            <Stack gap={theme.spacing(3)}>
                <TemplateEditorHeader
                    template={template}
                    valid={valid}
                    busy={busy}
                    onSave={() => void save()}
                    onDone={leave}
                />
                {mutation.isError && (
                    <Alert severity="error">
                        {t(
                            "settings.action_plan_templates.editor.save_failed",
                            "Unable to save the template. Check the fields and try again.",
                        )}
                        {Object.entries(errors)
                            .filter(([key]) => !["name", "sectorId"].includes(key))
                            .map(([key, value]) => (
                                <Typography key={`${key}-template-error`}>{value}</Typography>
                            ))}
                    </Alert>
                )}
                <TemplateGeneralInformationForm
                    template={template}
                    name={name}
                    selectedSector={selectedSector}
                    busy={busy}
                    errors={errors}
                    sectors={sectors}
                    sectorSearch={sectorSearch}
                    sectorDetached={sectorIsKnownToBeDetached}
                    onNameChange={setName}
                    onSectorChange={(sector) => {
                        setSelectedSector(sector);
                        setErrors((current) => {
                            if (!current.sectorId) return current;
                            const next = { ...current };
                            delete next.sectorId;
                            return next;
                        });
                    }}
                />
                <TemplateEditableActions
                    actions={actions}
                    ssqVersionId={template.ssqVersionId}
                    busy={busy}
                    onAdd={() => setDialog("add")}
                    onEdit={setDialog}
                    onRemove={(actionId) => setActions((current) => removeTemplateAction(current, actionId))}
                    onMove={(fromIndex, toIndex) =>
                        setActions((current) => moveTemplateAction(current, fromIndex, toIndex))
                    }
                />
            </Stack>
            {dialog !== null && (
                <TemplateActionDialog
                    ssqVersionId={template.ssqVersionId}
                    actions={actions}
                    editing={dialog === "add" ? undefined : dialog}
                    onClose={() => setDialog(null)}
                    onSave={(action) => {
                        setActions((current) => replaceTemplateAction(current, action));
                        setDialog(null);
                        snackbar.setSnackbarProps({
                            type: "success",
                            message:
                                dialog === "add" ?
                                    t(
                                        "settings.action_plan_templates.editor.action_added",
                                        "Action added to the template.",
                                    )
                                :   t("settings.action_plan_templates.editor.action_updated", "Action updated."),
                        });
                    }}
                />
            )}
            <TemplateUnsavedChangesDialog
                open={blocker.state === "blocked"}
                busy={busy}
                valid={valid}
                saveFailed={mutation.isError}
                onSave={() => void save()}
                onClose={() => blocker.reset?.()}
                onDiscard={() => blocker.proceed?.()}
            />
        </Box>
    );
};
